/**
 * Resolves the site content that is editable at runtime — currently the FAQ.
 *
 * Background. site-content.json used to be the single source for the FAQ:
 * the marketing app imported it for the visible list and the edge imported it
 * for the FAQPage JSON-LD, so the two could not contradict each other. That
 * property is what Google requires (schema that disagrees with the page it sits
 * on is discarded), and it survives here — what changed is only that the source
 * is now a database table ops can edit without a deploy (migration 108).
 *
 * Three render contexts, one answer:
 *
 *   1. Server-side render (entry-server.tsx, called by the edge per request and
 *      by the build-time prerender). The caller sets the override before
 *      render() and clears it after; there is no `window` to read.
 *   2. The browser. The edge injects `window.__EMBER_CONTENT__` into the
 *      document head next to the OG tags. This matters because the client boots
 *      with createRoot, not hydrateRoot — React REPLACES the server markup, so
 *      without the bootstrap the first paint would show the edited FAQ and the
 *      hydrated page would silently revert it to the build-time list.
 *   3. Neither — a plain `vite dev` preview, or an edge that could not reach
 *      the database. Falls through to the shipped file, which is exactly what
 *      the site rendered before any of this existed.
 *
 * The override is module-level mutable state, which is safe here only because
 * renderToString is synchronous: set → render → clear, with no await in
 * between. Do not reach for this from anything that yields.
 */

import SITE_CONTENT from './site-content.json';

export interface FaqItem {
  question: string;
  answer: string;
}

/**
 * Official profiles shown in the footer and emitted as Organization.sameAs.
 * Both surfaces intentionally read the same JSON array so a visible link can
 * never drift from the identity claim search engines receive.
 */
export const SOCIAL_PROFILE_URLS = Object.freeze([
  ...SITE_CONTENT.entity.sameAs,
]) as readonly string[];

/**
 * One /for/<slug> page, as the edge resolved it for this request.
 *
 * `body_html` is already rendered and already safe — the markdown was parsed
 * server-side through a token-level allowlist that cannot emit raw HTML (see
 * src/edge/content/markdown.js). That is what makes it acceptable to inject
 * with dangerouslySetInnerHTML, and it is the only reason: do not start
 * assigning this field from anywhere else.
 */
export interface UseCasePage {
  slug: string;
  /**
   * Author-supplied layout. When non-empty it REPLACES the built-in one.
   * Already templated, sanitised and CSS-scoped on the edge
   * (src/edge/content/page-html.js) — that is what makes injecting it
   * acceptable, and the only thing that does.
   */
  page_html?: string;
  group_key: string;
  title: string;
  description: string;
  hero_subtitle: string;
  image_url: string;
  body_html: string;
  excerpt: string;
  updated_at?: string;
}

export interface UseCaseGroup {
  key: string;
  label: string;
  blurb: string;
  pages: UseCasePage[];
}

/** One article, as the edge resolved it. `body_html` is pre-rendered and safe. */
export interface BlogPost {
  slug: string;
  /** Author-supplied layout; see the note on UseCasePage.page_html. */
  page_html?: string;
  title: string;
  description?: string;
  excerpt: string;
  image_url: string;
  author_name: string;
  author_image_url: string;
  category_name: string;
  category_slug: string;
  published_at: string;
  updated_at?: string;
  body_html?: string;
}

export interface BlogCategory {
  slug: string;
  name: string;
  description: string;
  published_count: number;
}

/** A page of the index or of a category archive. */
export interface BlogIndexData {
  posts: BlogPost[];
  page: number;
  pageCount: number;
  total: number;
  category: BlogCategory | null;
  categories: BlogCategory[];
}

export interface RuntimeSiteContent {
  faq?: FaqItem[];
  /** The page being rendered, on a /for/<slug> request. */
  useCase?: UseCasePage | null;
  /** Same-group pages to link to from a use-case page. */
  useCaseSiblings?: UseCasePage[];
  /** The hub's sections, on a /use-cases request. Empty groups already removed. */
  useCaseGroups?: UseCaseGroup[];
  /** The article being rendered, on a /blog/<slug> request. */
  blogPost?: BlogPost | null;
  /** Same-category articles to link to from an article page. */
  blogRelated?: BlogPost[];
  /** The listing, on /blog, /blog/page/N or /blog/category/<slug>. */
  blogIndex?: BlogIndexData | null;
  /** True when this render is an unpublished draft behind a preview token. */
  preview?: boolean;
}

/** The name the edge injects the bootstrap under. Shared with server.js. */
export const RUNTIME_CONTENT_GLOBAL = '__EMBER_CONTENT__';

let ssrOverride: RuntimeSiteContent | null = null;

/** SSR only: the content this render should use. Pass null to clear. */
export function setRuntimeSiteContent(content: RuntimeSiteContent | null): void {
  ssrOverride = content;
}

function readWindowContent(): RuntimeSiteContent | null {
  try {
    const injected = (globalThis as Record<string, unknown>)[RUNTIME_CONTENT_GLOBAL];
    if (injected && typeof injected === 'object') return injected as RuntimeSiteContent;
  } catch {
    /* a locked-down global object is not a reason to lose the FAQ */
  }
  return null;
}

/** Whichever content this render was given — SSR override first, then window. */
function runtimeContent(): RuntimeSiteContent {
  return ssrOverride ?? readWindowContent() ?? {};
}

/**
 * The use-case page for this render, or null.
 *
 * Null is a real state, not an error: the edge only ever routes a /for/<slug>
 * request here when it resolved a page, but the client bundle can also reach
 * this component after a soft navigation, and a missing bootstrap must render
 * an honest "not found" rather than a half-empty page.
 */
export function getUseCase(): UseCasePage | null {
  const page = runtimeContent().useCase;
  return page && typeof page.slug === 'string' && page.slug ? page : null;
}

export function getUseCaseSiblings(): UseCasePage[] {
  const siblings = runtimeContent().useCaseSiblings;
  return Array.isArray(siblings) ? siblings : [];
}

export function getBlogPost(): BlogPost | null {
  const post = runtimeContent().blogPost;
  return post && typeof post.slug === 'string' && post.slug ? post : null;
}

export function getBlogRelated(): BlogPost[] {
  const related = runtimeContent().blogRelated;
  return Array.isArray(related) ? related : [];
}

export function getBlogIndex(): BlogIndexData | null {
  return runtimeContent().blogIndex ?? null;
}

/** True when the page being rendered is an unpublished draft. */
export function isPreviewRender(): boolean {
  return runtimeContent().preview === true;
}
